<div class="device-actions-page">
    <section class="panel">
        <header class="panel-header">
            <div>
                <h2>Device Actions</h2>
                <p class="text-muted">Monitor pending and completed actions for this device.</p>
            </div>
            <div class="header-actions gap-2">
                <span *ngIf="loading()" class="text-muted text-sm">Refreshing…</span>
                <button pButton type="button" class="p-button-text" (click)="refresh()" [disabled]="loading()"><i class="pi pi-refresh p-button-icon" aria-hidden="true"></i><span class="p-button-label">Refresh</span></button>
                <button pButton type="button" (click)="openActionDialog()"><i class="pi pi-plus p-button-icon" aria-hidden="true"></i><span class="p-button-label">Add Action</span></button>
            </div>
        </header>
        <div class="panel-body">
            <div class="actions-timeline">
                <div *ngIf="error()" class="text-error mb-3">{{ error() }}</div>
                @if (!filteredActions().length && !loading()) {
                <app-empty-state icon="pi pi-inbox" title="No recent actions" description="Updates, restarts and other commands you queue for this system will be listed here." />
                }
                <app-action-item *ngFor="let action of filteredActions()" [action]="action"></app-action-item>

                <!-- Show "View All Actions" link if there are more actions -->
                <div *ngIf="hasMoreActions()" class="view-all-actions">
                    <button pButton type="button" class="p-button-text" (click)="openAllActionsDialog()"><i class="pi pi-list p-button-icon" aria-hidden="true"></i><span class="p-button-label">View All Actions</span></button>
                </div>
            </div>
        </div>
    </section>

    <section class="panel danger-zone" *ngIf="isSuperAdmin()">
        <header class="panel-header">
            <div>
                <h2>Migrate to Another Environment</h2>
                <p class="text-muted">Move this device to a different cloud environment (e.g. test → production). The device unclaims here, restarts, and re-registers with the target using a tenant API key.</p>
            </div>
            <div class="header-actions">
                <button pButton type="button" class="p-button-danger p-button-outlined" (click)="showMigrateDialog.set(true)"><i class="pi pi-cloud-upload p-button-icon" aria-hidden="true"></i><span class="p-button-label">Migrate Device</span></button>
            </div>
        </header>
    </section>
</div>

<app-migrate-cloud-dialog [(visible)]="showMigrateDialog"></app-migrate-cloud-dialog>

<!-- Dialog for creating new action -->
<p-dialog [(visible)]="showCreateDialog" [modal]="true" [closable]="true" [style]="{width: '600px', maxHeight: '90vh'}" header="Create Device Action" (onHide)="resetForm()">
    <div class="workflow">
        <div class="workflow-summary">
            <div>
                <span class="summary-label">Selected Action</span>
                <span class="summary-value">{{ actionType() }}</span>
            </div>
            <div>
                <span class="summary-label">Schedule</span>
                <span class="summary-value">{{ scheduleSummary() }}</span>
            </div>
        </div>

        <p-stepper [linear]="true" [value]="stepIndex()" (valueChange)="stepIndex.set($event ?? 1)" class="action-stepper">
            <p-step-list>
                <p-step [value]="1">Select Action</p-step>
                <p-step [value]="2">Schedule</p-step>
                <p-step [value]="3">Options</p-step>
            </p-step-list>

            <p-step-panels>
                <p-step-panel [value]="1">
                    <ng-template #content>
                        <div class="step-content action-step">
                            <p-listbox
                                inputId="actionTypeListbox"
                                [options]="actionOptions()"
                                optionLabel="label"
                                optionValue="value"
                                [checkmark]="true"
                                [ngModel]="actionType()"
                                (ngModelChange)="actionType.set($event)"
                                [metaKeySelection]="false"
                                class="action-type-listbox"
                            ></p-listbox>
                        </div>
                        <div class="step-footer">
                            <span></span>
                            <button pButton type="button" (click)="nextStep()"><span class="p-button-label">Next</span><i class="pi pi-arrow-right p-button-icon" aria-hidden="true"></i></button>
                        </div>
                    </ng-template>
                </p-step-panel>

                <p-step-panel [value]="2">
                    <ng-template #content>
                        <div class="step-content schedule-step">
                            <p-datepicker [ngModel]="scheduledFor()" (ngModelChange)="onScheduleChange($event)" [showTime]="true" hourFormat="24" [stepMinute]="5" [inline]="true"></p-datepicker>
                            <div class="schedule-controls">
                                <button pButton type="button" class="p-button-text" (click)="clearSchedule()"><i class="pi pi-clock p-button-icon" aria-hidden="true"></i><span class="p-button-label">Dispatch on next check-in</span></button>
                                <small class="text-muted">Leave unset to send the action as soon as the device checks in.</small>
                            </div>
                            <small *ngIf="scheduleError()" class="text-error">{{ scheduleError() }}</small>
                        </div>
                        <div class="step-footer">
                            <button pButton type="button" class="p-button-text" (click)="previousStep()"><i class="pi pi-arrow-left p-button-icon" aria-hidden="true"></i><span class="p-button-label">Back</span></button>
                            <button pButton type="button" (click)="nextStep()"><span class="p-button-label">Next</span><i class="pi pi-arrow-right p-button-icon" aria-hidden="true"></i></button>
                        </div>
                    </ng-template>
                </p-step-panel>

                <p-step-panel [value]="3">
                    <ng-template #content>
                        <div class="step-content options-step">
                            <!-- Update action: show version selector with badges -->
                            <ng-container *ngIf="actionType() === 'Update'">
                                <div *ngIf="currentDeviceVersion()" class="current-version-info">
                                    <span class="current-version-label">Current version on device:</span>
                                    <span class="current-version-value">{{ currentDeviceVersion() }}</span>
                                </div>
                                <label class="form-label" for="versionSelect">Select Package Version to Deploy</label>
                                <p-select
                                    inputId="versionSelect"
                                    [options]="versions()"
                                    optionLabel="label"
                                    optionValue="value"
                                    [ngModel]="selectedVersionKey()"
                                    (ngModelChange)="selectedVersionKey.set($event)"
                                    placeholder="Select a version..."
                                    [loading]="versionsLoading()"
                                    [filter]="true"
                                    filterPlaceholder="Search versions..."
                                    [virtualScroll]="versions().length > 100"
                                    [virtualScrollItemSize]="40"
                                    appendTo="body"
                                    class="w-full">
                                    <ng-template #selectedItem let-item>
                                        <div class="version-option" *ngIf="item">
                                            <span>{{ item.label }}</span>
                                            <p-tag *ngIf="item.isLatest" value="Latest" severity="success" class="ml-2"></p-tag>
                                            <p-tag *ngIf="item.isDebugBuild" value="Debug" severity="warn" class="ml-1"></p-tag>
                                            <p-tag *ngIf="!item.isDebugBuild" value="Release" severity="info" class="ml-1"></p-tag>
                                        </div>
                                    </ng-template>
                                    <ng-template #item let-item>
                                        <div class="version-option">
                                            <span>{{ item.label }}</span>
                                            <p-tag *ngIf="item.isLatest" value="Latest" severity="success" class="ml-2"></p-tag>
                                            <p-tag *ngIf="item.isDebugBuild" value="Debug" severity="warn" class="ml-1"></p-tag>
                                            <p-tag *ngIf="!item.isDebugBuild" value="Release" severity="info" class="ml-1"></p-tag>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <small *ngIf="versionsError()" class="text-error">{{ versionsError() }}</small>
                                <small *ngIf="!versionsLoading() && versions().length === 0 && !versionsError()" class="text-muted"> No compatible packages found. Only packages matching this device's application namespace are shown. </small>
                            </ng-container>

                            <!-- Firmware Update action: show firmware version selector -->
                            <ng-container *ngIf="actionType() === 'FirmwareUpdate'">
                                <div *ngIf="currentFirmwareVersion()" class="current-version-info">
                                    <span class="current-version-label">Current firmware version:</span>
                                    <span class="current-version-value">{{ currentFirmwareVersion() }}</span>
                                </div>
                                <label class="form-label" for="firmwareSelect">Select Firmware Version</label>
                                <p-select
                                    inputId="firmwareSelect"
                                    [options]="firmwareVersions()"
                                    optionLabel="label"
                                    optionValue="value"
                                    [ngModel]="selectedFirmwareKey()"
                                    (ngModelChange)="selectedFirmwareKey.set($event)"
                                    placeholder="Select a firmware version..."
                                    [loading]="firmwareVersionsLoading()"
                                    [filter]="true"
                                    filterPlaceholder="Search firmware..."
                                    appendTo="body"
                                    class="w-full">
                                    <ng-template #selectedItem let-item>
                                        <div class="version-option" *ngIf="item">
                                            <span>{{ item.label }}</span>
                                            <p-tag *ngIf="item.isCurrent" value="Current" severity="info" class="ml-2"></p-tag>
                                            <p-tag *ngIf="!item.isCurrent" value="Upgrade" severity="success" class="ml-2"></p-tag>
                                        </div>
                                    </ng-template>
                                    <ng-template #item let-item>
                                        <div class="version-option">
                                            <span>{{ item.label }}</span>
                                            <p-tag *ngIf="item.isCurrent" value="Current" severity="info" class="ml-2"></p-tag>
                                            <p-tag *ngIf="!item.isCurrent" value="Upgrade" severity="success" class="ml-2"></p-tag>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <small *ngIf="firmwareVersionsError()" class="text-error">{{ firmwareVersionsError() }}</small>
                                <small *ngIf="!firmwareVersionsLoading() && firmwareVersions().length === 0 && !firmwareVersionsError()" class="text-muted"> No firmware updates available for this device model. </small>
                            </ng-container>

                            <!-- Reboot/Restart actions: no options needed -->
                            <ng-container *ngIf="actionType() === 'Reboot' || actionType() === 'Restart'">
                                <div class="no-options-message">
                                    <i class="pi pi-check-circle"></i>
                                    <span>No additional options required for this action.</span>
                                </div>
                            </ng-container>
                        </div>
                        <div class="step-footer">
                            <button pButton type="button" class="p-button-text" (click)="previousStep()"><i class="pi pi-arrow-left p-button-icon" aria-hidden="true"></i><span class="p-button-label">Back</span></button>
                            <div class="step-actions">
                                <button pButton type="button" class="p-button-text" (click)="resetForm()" [disabled]="submitting()"><i class="pi pi-refresh p-button-icon" aria-hidden="true"></i><span class="p-button-label">Reset</span></button>
                                <button
                                    pButton
                                    type="button"
                                    class="p-button-primary"
                                    (click)="queueAction()"
                                    [disabled]="submitting() || (actionType() === 'Update' && !selectedVersionKey()) || (actionType() === 'FirmwareUpdate' && !selectedFirmwareKey())"><i class="pi pi-check p-button-icon" aria-hidden="true"></i><span class="p-button-label">Queue Action</span></button>
                            </div>
                        </div>
                    </ng-template>
                </p-step-panel>
            </p-step-panels>
        </p-stepper>

        <div *ngIf="creationError()" class="text-error">{{ creationError() }}</div>
    </div>
</p-dialog>

<!-- Dialog for viewing all actions -->
<p-dialog [(visible)]="showAllActionsDialog" [modal]="true" [closable]="true" [style]="{width: '800px', maxHeight: '90vh'}" header="All Device Actions">
    <div class="all-actions-list">
        <app-action-item *ngFor="let action of actions()" [action]="action"></app-action-item>
    </div>
</p-dialog>
